import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { DailySummarySettingsForm, useDailySummarySettings } from '../components/DailySummarySettings';
import {
  getSettings,
  updateSettings,
  listChats,
  updateChat,
  getRivhitConfig,
  getHeshbonitConfig,
  getGreenInvoiceConfig,
  updateRivhitConfig,
  updateHeshbonitConfig,
  updateGreenInvoiceConfig,
  testRivhitConfig,
  testHeshbonitConfig,
  testGreenInvoiceConfig,
  getRivhitSyncStatus,
  resyncRivhitData,
  getHeshbonitSyncStatus,
  resyncHeshbonitData,
  getGreenInvoiceSyncStatus,
  resyncGreenInvoiceData,
  runOrderSuggestions,
  getPermittedNumbers,
  type PermittedNumber,
  getBusinessAnalysisSchedule,
  updateBusinessAnalysisSchedule,
  sendBusinessAnalysisWhatsAppNow,
  syncRivhitCustomerPaymentTerms,
  updateRivhitCustomerSettings,
  getSmsSettings,
  updateSmsSettings,
  startPermittedNumberVerification,
  updatePermittedNumber,
  deletePermittedNumber,
  listMembers,
  inviteMember,
  updateMemberRole,
  removeMember,
  approveAeNotifications,
  resendWaApproval,
  forceWaApproval,
  getMcpKeyStatus,
  generateMcpKey,
  revokeMcpKey,
  type TenantMember,
  type SmsSettings,
  type McpKeyStatus,
  ModuleId,
} from '../api/client';
import { useRivhitData, rivhitDisplayName } from '../context/RivhitDataContext';
import { useModules } from '../context/ModulesContext';
import { useAuth } from '../context/AuthContext';
import { LanguageSwitcher } from '../components';
import PaymentLinkSettings from '../components/PaymentLinkSettings';
import MessageTemplatesSettings from '../components/MessageTemplatesSettings';
import { featureFlags } from '../lib/featureFlags';
import ToggleSwitch from '../components/ToggleSwitch';
import UserUsageAnalytics from '../components/UserUsageAnalytics';
import { canViewUserUsage } from '../lib/userUsage';

/**
 * Hard cap on how long the sync-status pollers keep running. If the backend
 * status stays `in_progress`/`unknown` indefinitely (stuck/lost sync job), the
 * 3s interval would otherwise poll forever, driving constant API/Lambda calls
 * for every open Settings tab. After this deadline we stop and surface an error.
 */
const MAX_SYNC_POLL_DURATION_MS = 5 * 60 * 1000;

interface ChatConfig {
  id: string;
  name: string;
  chat_type: string;
  priority: string;
  is_monitored: boolean;
  is_order_analysis_group?: boolean;
  sensitivity: number;
  participants_count?: number;
  in_database?: boolean;
  last_order_suggestions_run_at?: string;
}

interface AppSettings {
  business_name: string;
  bank_transfer_details: string;
  language: string;
  timezone: string;
  business_hours_start: string;
  business_hours_end: string;
  auto_create_reminders: boolean;
  chat_monitoring_enabled: boolean;
  invoice_provider: 'rivhit' | 'heshbonit_beclick' | 'green_invoice';
  payment_links_enabled: boolean;
  qr_fallback_customer_id: string;
  qr_fallback_customer_name: string;
  sms_payment_notifications_enabled: boolean;
  sms_whatsapp_handoff_enabled: boolean;
  default_payment_request_expiration_days: number;
  customer_bot_enabled: boolean;
  customer_bot_business_name: string;
  customer_bot_greeting_template: string;
  sms_sender_id: string;
  opt_in_message_template: string;
  opt_in_consent_message_version: string;
  automations_enabled: boolean;
  collections: {
    hourly_invoice_debt_check_enabled: boolean;
    debt_age_threshold_days: number;
    invoice_block_debt_age_days: number;
  };
  min_confidence_threshold: number;
  noise_sensitivity: number;
  high_priority_keywords: string[];
  priority_customers: string[];
  priority_suppliers: string[];
  whatsapp_delivery_channel: 'whatsapp_link' | 'twilio';
  whatsapp_message_delivery_channels: {
    debt_open_balance: 'whatsapp_link' | 'twilio';
    document_link: 'whatsapp_link' | 'twilio';
  };
}

interface RivhitTestResult {
  success: boolean;
  message: string;
}

const defaultSettings: AppSettings = {
  business_name: 'Dada Coffee',
  bank_transfer_details: '',
  language: 'en',
  timezone: 'Asia/Jerusalem',
  business_hours_start: '08:00',
  business_hours_end: '18:00',
  auto_create_reminders: true,
  chat_monitoring_enabled: true,
  invoice_provider: 'rivhit',
  payment_links_enabled: false,
  qr_fallback_customer_id: '',
  qr_fallback_customer_name: '',
  sms_payment_notifications_enabled: false,
  sms_whatsapp_handoff_enabled: true,
  default_payment_request_expiration_days: 14,
  customer_bot_enabled: false,
  customer_bot_business_name: '',
  customer_bot_greeting_template: '',
  sms_sender_id: '',
  opt_in_message_template: '',
  opt_in_consent_message_version: '',
  automations_enabled: true,
  collections: {
    hourly_invoice_debt_check_enabled: false,
    debt_age_threshold_days: 60,
    invoice_block_debt_age_days: 90,
  },
  min_confidence_threshold: 0.6,
  noise_sensitivity: 0.5,
  high_priority_keywords: [],
  priority_customers: [],
  priority_suppliers: [],
  whatsapp_delivery_channel: 'whatsapp_link',
  whatsapp_message_delivery_channels: {
    debt_open_balance: 'whatsapp_link',
    document_link: 'whatsapp_link',
  },
};

/* ── MemberEditPanel sub-component ── */

interface MemberEditPanelProps {
  member: TenantMember;
  canSetRole: boolean;
  onSave: (updates: {
    name?: string;
    email?: string;
    phone_number?: string;
    role?: string;
  }) => void;
  onCancel: () => void;
}

function MemberEditPanel({ member, canSetRole, onSave, onCancel }: MemberEditPanelProps) {
  const isNamed = member.user_id.startsWith('named:');
  const [name, setName] = useState(member.name || '');
  const [email, setEmail] = useState(member.email || '');
  const [phone, setPhone] = useState(member.phone_number || '');
  const [role, setRole] = useState(member.role);

  const handleSave = () => {
    const updates: Parameters<typeof onSave>[0] = {};
    if (name !== (member.name || '')) updates.name = name;
    if (isNamed && email !== (member.email || '')) updates.email = email;
    if (phone !== (member.phone_number || '')) updates.phone_number = phone;
    if (canSetRole && role !== member.role) updates.role = role;
    onSave(updates);
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 grid gap-3 sm:grid-cols-2">
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-gray-500 uppercase tracking-wide">Name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </label>
      {isNamed && (
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500 uppercase tracking-wide">Email (for workspace login)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@gmail.com"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          />
        </label>
      )}
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-gray-500 uppercase tracking-wide">Phone (for WhatsApp/SMS)</span>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+972501234567"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </label>
      {canSetRole && (
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500 uppercase tracking-wide">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="admin">Admin</option>
            <option value="member">Member</option>
            <option value="account_executive">Account Executive</option>
          </select>
        </label>
      )}
      <div className={`flex items-center gap-2 ${isNamed ? 'sm:col-span-2' : ''}`}>
        <button
          type="button"
          onClick={handleSave}
          className="rounded-md bg-primary-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ── AccordionCard sub-component ── */

interface AccordionCardProps {
  icon?: string;
  title: string;
  description?: string;
  defaultOpen?: boolean;
  moduleId?: ModuleId;
  children: React.ReactNode;
}

function AccordionCard({ icon, title, description, defaultOpen = false, moduleId, children }: AccordionCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const { modules, setModuleEnabled } = useModules();
  const [toggling, setToggling] = useState(false);

  const module = moduleId ? modules.find(m => m.id === moduleId) : undefined;

  const handleModuleToggle = async (e: React.MouseEvent, val: boolean) => {
    e.stopPropagation();
    if (!module || module.always_on) return;
    setToggling(true);
    try {
      await setModuleEnabled(moduleId!, val);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className={`rounded-lg border bg-white shadow-sm transition-all ${open ? 'border-primary-200' : 'border-gray-200'}`}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
      >
        {icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100 text-lg">
            {icon}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-gray-900">{title}</span>
            {module?.always_on && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{t('settings.moduleAlwaysOn', 'Always on')}</span>
            )}
            {module && !module.always_on && (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${module.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {module.enabled ? t('settings.moduleEnabled', 'Enabled') : t('settings.moduleDisabled', 'Disabled')}
              </span>
            )}
          </div>
          {description && <p className="mt-0.5 text-sm text-gray-500 truncate">{description}</p>}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Module enable/disable toggle in header */}
          {module && !module.always_on && (
            <span onClick={(e) => e.stopPropagation()}>
              <ToggleSwitch
                checked={module.enabled}
                onChange={(val) => handleModuleToggle({ stopPropagation: () => {} } as React.MouseEvent, val)}
                disabled={toggling}
                size="sm"
                aria-label={`Toggle ${title} module`}
              />
            </span>
          )}
          {/* Chevron */}
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Content */}
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100 space-y-5">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { reloadAfterConfigChange, customers, customersLoading, ensureCustomersLoaded, refreshCustomers } = useRivhitData();
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [chats, setChats] = useState<ChatConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [runningAnalysisChatId, setRunningAnalysisChatId] = useState<string | null>(null);

  // SMS settings state
  const [smsSettings, setSmsSettings] = useState<SmsSettings | null>(null);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsSaved, setSmsSaved] = useState(false);

  // Rivhit integration state
  const [rivhitHasToken, setRivhitHasToken] = useState(false);
  const [rivhitHasCredentials, setRivhitHasCredentials] = useState(false);
  const [rivhitHasPaymentPageToken, setRivhitHasPaymentPageToken] = useState(false);
  const [rivhitToken, setRivhitToken] = useState('');
  const [rivhitPaymentPageToken, setRivhitPaymentPageToken] = useState('');
  const [rivhitUsername, setRivhitUsername] = useState('');
  const [rivhitPassword, setRivhitPassword] = useState('');
  const [rivhitIdCompany, setRivhitIdCompany] = useState('');
  const [rivhitSaving, setRivhitSaving] = useState(false);
  const [rivhitSaved, setRivhitSaved] = useState(false);
  const [rivhitTesting, setRivhitTesting] = useState(false);
  const [rivhitTestResult, setRivhitTestResult] = useState<RivhitTestResult | null>(null);
  const [rivhitError, setRivhitError] = useState('');
  const [rivhitResyncing, setRivhitResyncing] = useState(false);
  const [rivhitResyncDone, setRivhitResyncDone] = useState(false);
  const [rivhitResyncModalOpen, setRivhitResyncModalOpen] = useState(false);
  const [rivhitSyncing, setRivhitSyncing] = useState(false);
  const [heshbonitHasCredentials, setHeshbonitHasCredentials] = useState(false);
  const [heshbonitUsername, setHeshbonitUsername] = useState('');
  const [heshbonitPassword, setHeshbonitPassword] = useState('');
  const [heshbonitSaving, setHeshbonitSaving] = useState(false);
  const [heshbonitSaved, setHeshbonitSaved] = useState(false);
  const [heshbonitTesting, setHeshbonitTesting] = useState(false);
  const [heshbonitTestResult, setHeshbonitTestResult] = useState<RivhitTestResult | null>(null);
  const [heshbonitError, setHeshbonitError] = useState('');
  const [heshbonitSyncing, setHeshbonitSyncing] = useState(false);
  const [heshbonitResyncDone, setHeshbonitResyncDone] = useState(false);
  const [heshbonitSyncError, setHeshbonitSyncError] = useState('');
  const [greenInvoiceHasCredentials, setGreenInvoiceHasCredentials] = useState(false);
  const [greenInvoiceEnvironment, setGreenInvoiceEnvironment] = useState('sandbox');
  const [greenInvoiceApiKeyId, setGreenInvoiceApiKeyId] = useState('');
  const [greenInvoiceApiSecret, setGreenInvoiceApiSecret] = useState('');
  const [greenInvoiceBusinessId, setGreenInvoiceBusinessId] = useState('');
  const [greenInvoiceSaving, setGreenInvoiceSaving] = useState(false);
  const [greenInvoiceSaved, setGreenInvoiceSaved] = useState(false);
  const [greenInvoiceTesting, setGreenInvoiceTesting] = useState(false);
  const [greenInvoiceTestResult, setGreenInvoiceTestResult] = useState<RivhitTestResult | null>(null);
  const [greenInvoiceError, setGreenInvoiceError] = useState('');
  const [greenInvoiceSyncing, setGreenInvoiceSyncing] = useState(false);
  const [greenInvoiceResyncDone, setGreenInvoiceResyncDone] = useState(false);
  const [greenInvoiceSyncError, setGreenInvoiceSyncError] = useState('');
  const [paymentTermsSyncing, setPaymentTermsSyncing] = useState(false);
  const [paymentTermsSavingId, setPaymentTermsSavingId] = useState<number | null>(null);
  const [paymentTermsMessage, setPaymentTermsMessage] = useState('');
  const [paymentTermsError, setPaymentTermsError] = useState('');
  const [paymentTermsSearch, setPaymentTermsSearch] = useState('');
  const [qrFallbackSearch, setQrFallbackSearch] = useState('');
  const syncPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncPollingDeadlineRef = useRef<number>(0);
  const heshbonitSyncPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heshbonitSyncPollingDeadlineRef = useRef<number>(0);
  const greenInvoiceSyncPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const greenInvoiceSyncPollingDeadlineRef = useRef<number>(0);
  const [permittedNumbers, setPermittedNumbers] = useState<PermittedNumber[]>([]);
  const [permittedLoading, setPermittedLoading] = useState(false);
  const [permittedPhone, setPermittedPhone] = useState('');
  const [permittedMessage, setPermittedMessage] = useState('');
  const [permittedError, setPermittedError] = useState('');
  const dailySummary = useDailySummarySettings(permittedNumbers, permittedLoading);

  // Business analysis schedule state
  const [baDay, setBaDay] = useState(0);
  const [baHour, setBaHour] = useState(8);
  const [baMinute, setBaMinute] = useState(0);
  const [baTimezone, setBaTimezone] = useState('Asia/Jerusalem');
  const [baLoading, setBaLoading] = useState(false);
  const [baMessage, setBaMessage] = useState('');
  const [baError, setBaError] = useState('');
  const [baSending, setBaSending] = useState(false);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [memberMessage, setMemberMessage] = useState('');
  const [memberError, setMemberError] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteSaving, setInviteSaving] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [expandedMemberIds, setExpandedMemberIds] = useState<Set<string>>(new Set());
  const [waActionLoading, setWaActionLoading] = useState<string | null>(null);
  const [aeApprovalLoading, setAeApprovalLoading] = useState<string | null>(null);
  const [reminderPreferenceSaving, setReminderPreferenceSaving] = useState<string | null>(null);
  const canManageMembers = user?.role === 'owner' || user?.role === 'admin';
  const canViewUsage = canViewUserUsage(user?.role);
  const canInviteAndSetRole = user?.role === 'owner';
  const [settingsTab, setSettingsTab] = useState<'configuration' | 'usage'>('configuration');

  // MCP API key state
  const [mcpKeyStatus, setMcpKeyStatus] = useState<McpKeyStatus | null>(null);
  const [mcpGeneratedKey, setMcpGeneratedKey] = useState<string | null>(null);
  const [mcpKeyCopied, setMcpKeyCopied] = useState(false);
  const [mcpConfigCopied, setMcpConfigCopied] = useState(false);
  const [mcpKeyLoading, setMcpKeyLoading] = useState(false);
  const [mcpKeyError, setMcpKeyError] = useState('');
  const [mcpRevokeConfirm, setMcpRevokeConfirm] = useState(false);
  const [mcpTarget, setMcpTarget] = useState<'desktop' | 'web'>('desktop');

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, chatsRes, rivhitRes, heshbonitRes, greenInvoiceRes, permittedRes, baScheduleRes, smsRes, membersRes, mcpStatusRes] = await Promise.all([
        getSettings(),
        listChats(false),
        getRivhitConfig().catch(() => null),
        getHeshbonitConfig().catch(() => null),
        getGreenInvoiceConfig().catch(() => null),
        getPermittedNumbers().catch(() => null),
        getBusinessAnalysisSchedule().catch(() => null),
        getSmsSettings().catch(() => null),
        user?.tenant_id ? listMembers(user.tenant_id).catch(() => null) : Promise.resolve(null),
        getMcpKeyStatus().catch(() => null),
      ]);
      const s = settingsRes.data?.settings ?? settingsRes.data ?? {};
      setSettings({ ...defaultSettings, ...s });
      setChats(chatsRes.data?.chats ?? chatsRes.data ?? []);
      if (rivhitRes?.data) {
        setRivhitHasToken(rivhitRes.data.has_token ?? false);
        setRivhitHasCredentials(rivhitRes.data.has_credentials ?? false);
        setRivhitHasPaymentPageToken(rivhitRes.data.has_payment_page_token ?? false);
      }
      if (heshbonitRes?.data) {
        setHeshbonitHasCredentials(heshbonitRes.data.has_credentials ?? false);
      }
      if (greenInvoiceRes?.data) {
        setGreenInvoiceHasCredentials(greenInvoiceRes.data.has_credentials ?? false);
        setGreenInvoiceEnvironment(greenInvoiceRes.data.environment ?? 'sandbox');
      }
      if (permittedRes?.data) {
        setPermittedNumbers(permittedRes.data.numbers || []);
      }
      if (baScheduleRes?.data) {
        setBaDay(baScheduleRes.data.send_day ?? 0);
        setBaHour(baScheduleRes.data.send_hour ?? 8);
        setBaMinute(baScheduleRes.data.send_minute ?? 0);
        setBaTimezone(baScheduleRes.data.timezone ?? 'Asia/Jerusalem');
      }
      if (smsRes?.data) {
        setSmsSettings(smsRes.data);
      }
      if (membersRes?.members) {
        setMembers(membersRes.members);
      }
      if (mcpStatusRes?.data) {
        setMcpKeyStatus(mcpStatusRes.data);
      }
    } catch { /* use defaults */ }
    finally { setLoading(false); }
  }, [user?.tenant_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reloadMembers = useCallback(async () => {
    if (!user?.tenant_id) return;
    const result = await listMembers(user.tenant_id);
    setMembers(result.members);
  }, [user?.tenant_id]);

  const handleApproveAeNotifications = async (member: TenantMember) => {
    if (!user?.tenant_id || aeApprovalLoading) return;
    if (!window.confirm(t('settings.aeApprovalConfirm', { name: member.name || member.email || member.user_id }))) return;
    setAeApprovalLoading(member.user_id);
    setMemberError('');
    setMemberMessage('');
    try {
      const updated = await approveAeNotifications(user.tenant_id, member.user_id);
      setMembers((current) => current.map((row) => row.user_id === member.user_id ? updated : row));
      setMemberMessage(t('settings.aeApprovalSuccess'));
    } catch (err) {
      const detail = isAxiosError(err) && typeof err.response?.data?.detail === 'string' ? err.response.data.detail : '';
      setMemberError(detail || t('settings.aeApprovalError'));
    } finally {
      setAeApprovalLoading(null);
    }
  };

  const handleInviteMember = async () => {
    if (!user?.tenant_id || (!inviteName.trim() && !inviteEmail.trim())) return;
    setInviteSaving(true);
    setMemberError('');
    setMemberMessage('');
    try {
      await inviteMember(user.tenant_id, inviteRole, {
        name: inviteName.trim() || undefined,
        email: inviteEmail.trim() || undefined,
        ae_query_scope: 'assigned',
        assigned_customer_ids: [],
        ae_whatsapp_notifications_enabled: inviteRole === 'account_executive',
        ...(invitePhone.trim() ? { phone_number: invitePhone.trim() } : {}),
      });
      setInviteName('');
      setInviteEmail('');
      setInviteRole('member');
      setInvitePhone('');
      const msg = inviteEmail.trim()
        ? 'Member added. They will join the workspace after signing in with Google.'
        : 'Member added. Add their email later so they can log in.';
      setMemberMessage(msg);
      await reloadMembers();
    } catch (err: any) {
      setMemberError(err?.response?.data?.detail || 'Failed to add member');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleMemberUpdate = async (
    member: TenantMember,
    updates: {
      role?: string;
      name?: string;
      email?: string;
      phone_number?: string;
      assigned_customer_ids?: number[];
      ae_query_scope?: 'assigned' | 'all';
      ae_whatsapp_notifications_enabled?: boolean;
      customer_reminder_notifications_enabled?: boolean;
    },
  ) => {
    if (!user?.tenant_id) return;
    setMemberError('');
    setMemberMessage('');
    try {
      const updated = await updateMemberRole(user.tenant_id, member.user_id, updates);
      setMembers((current) => current.map((m) => (m.user_id === updated.user_id ? updated : m)));
      setMemberMessage('Member settings saved.');
    } catch (err: any) {
      setMemberError(err?.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleRemoveMember = async (member: TenantMember) => {
    if (!user?.tenant_id) return;
    const displayName = member.name || member.email || member.user_id;
    if (!window.confirm(`Remove "${displayName}" from the workspace? This will also remove all their customer assignments.`)) return;
    setMemberError('');
    setMemberMessage('');
    try {
      await removeMember(user.tenant_id, member.user_id);
      setMembers((current) => current.filter((m) => m.user_id !== member.user_id));
      setMemberMessage(`${displayName} has been removed.`);
    } catch (err: any) {
      setMemberError(err?.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleResendWaApproval = async (phoneNumber: string) => {
    setWaActionLoading(`resend:${phoneNumber}`);
    setMemberError('');
    setMemberMessage('');
    try {
      await resendWaApproval(phoneNumber);
      setMemberMessage(`Verification message resent to ${phoneNumber}.`);
    } catch (err: any) {
      setMemberError(err?.response?.data?.detail || 'Failed to resend verification');
    } finally {
      setWaActionLoading(null);
    }
  };

  const handleForceWaApproval = async (phoneNumber: string) => {
    if (!window.confirm(
      `Force approve ${phoneNumber}?\n\nThis will mark the number as verified immediately without sending a WhatsApp message to the user.`
    )) return;
    setWaActionLoading(`force:${phoneNumber}`);
    setMemberError('');
    setMemberMessage('');
    try {
      await forceWaApproval(phoneNumber);
      setMemberMessage(`${phoneNumber} has been force-approved.`);
      await reloadMembers();
    } catch (err: any) {
      setMemberError(err?.response?.data?.detail || 'Failed to force approve');
    } finally {
      setWaActionLoading(null);
    }
  };

  // Poll sync status and keep rivhitSyncing indicator alive until completion
  const stopSyncPolling = useCallback(() => {
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }
  }, []);

  const startSyncPolling = useCallback(() => {
    stopSyncPolling();
    setRivhitSyncing(true);
    syncPollingDeadlineRef.current = Date.now() + MAX_SYNC_POLL_DURATION_MS;
    syncPollingRef.current = setInterval(async () => {
      if (Date.now() > syncPollingDeadlineRef.current) {
        setRivhitSyncing(false);
        setRivhitError(t('settings.rivhit.syncError'));
        stopSyncPolling();
        return;
      }
      try {
        const res = await getRivhitSyncStatus();
        const status = res.data?.status;
        if (status === 'complete') {
          setRivhitSyncing(false);
          stopSyncPolling();
          reloadAfterConfigChange();
        } else if (status === 'error') {
          setRivhitSyncing(false);
          setRivhitError(res.data?.message || t('settings.rivhit.syncError'));
          stopSyncPolling();
        }
        // 'in_progress' or 'unknown' → keep polling
      } catch {
        // Ignore transient errors, keep polling
      }
    }, 3000);
  }, [stopSyncPolling, reloadAfterConfigChange, t]);

  const stopHeshbonitSyncPolling = useCallback(() => {
    if (heshbonitSyncPollingRef.current) {
      clearInterval(heshbonitSyncPollingRef.current);
      heshbonitSyncPollingRef.current = null;
    }
  }, []);

  const startHeshbonitSyncPolling = useCallback(() => {
    stopHeshbonitSyncPolling();
    setHeshbonitSyncing(true);
    heshbonitSyncPollingDeadlineRef.current = Date.now() + MAX_SYNC_POLL_DURATION_MS;
    heshbonitSyncPollingRef.current = setInterval(async () => {
      if (Date.now() > heshbonitSyncPollingDeadlineRef.current) {
        setHeshbonitSyncing(false);
        setHeshbonitSyncError('Failed to load data from Heshbonit BeClick');
        stopHeshbonitSyncPolling();
        return;
      }
      try {
        const res = await getHeshbonitSyncStatus();
        const status = res.data?.status;
        if (status === 'complete') {
          setHeshbonitSyncing(false);
          stopHeshbonitSyncPolling();
          setHeshbonitResyncDone(true);
          setTimeout(() => setHeshbonitResyncDone(false), 4000);
          reloadAfterConfigChange();
        } else if (status === 'error') {
          setHeshbonitSyncing(false);
          setHeshbonitSyncError(res.data?.message || 'Failed to load data from Heshbonit BeClick');
          stopHeshbonitSyncPolling();
        }
        // 'in_progress' or 'unknown' → keep polling
      } catch {
        // Ignore transient errors, keep polling
      }
    }, 3000);
  }, [stopHeshbonitSyncPolling, reloadAfterConfigChange]);

  const stopGreenInvoiceSyncPolling = useCallback(() => {
    if (greenInvoiceSyncPollingRef.current) {
      clearInterval(greenInvoiceSyncPollingRef.current);
      greenInvoiceSyncPollingRef.current = null;
    }
  }, []);

  const startGreenInvoiceSyncPolling = useCallback(() => {
    stopGreenInvoiceSyncPolling();
    setGreenInvoiceSyncing(true);
    greenInvoiceSyncPollingDeadlineRef.current = Date.now() + MAX_SYNC_POLL_DURATION_MS;
    greenInvoiceSyncPollingRef.current = setInterval(async () => {
      if (Date.now() > greenInvoiceSyncPollingDeadlineRef.current) {
        setGreenInvoiceSyncing(false);
        setGreenInvoiceSyncError('Failed to load data from Green Invoice');
        stopGreenInvoiceSyncPolling();
        return;
      }
      try {
        const res = await getGreenInvoiceSyncStatus();
        const status = res.data?.status;
        if (status === 'complete') {
          setGreenInvoiceSyncing(false);
          stopGreenInvoiceSyncPolling();
          setGreenInvoiceResyncDone(true);
          setTimeout(() => setGreenInvoiceResyncDone(false), 4000);
          reloadAfterConfigChange();
        } else if (status === 'error') {
          setGreenInvoiceSyncing(false);
          setGreenInvoiceSyncError(res.data?.message || 'Failed to load data from Green Invoice');
          stopGreenInvoiceSyncPolling();
        }
      } catch {
        // Ignore transient errors, keep polling
      }
    }, 3000);
  }, [stopGreenInvoiceSyncPolling, reloadAfterConfigChange]);

  // On mount, check if a sync is already in progress
  useEffect(() => {
    if (loading) return; // wait until settings are loaded from the API
    if (settings.invoice_provider !== 'heshbonit_beclick') {
      getRivhitSyncStatus().then(res => {
        if (res.data?.status === 'in_progress') {
          startSyncPolling();
        }
      }).catch(() => { /* ignore */ });
    }
    if (settings.invoice_provider === 'heshbonit_beclick') {
      getHeshbonitSyncStatus().then(res => {
        if (res.data?.status === 'in_progress') {
          startHeshbonitSyncPolling();
        }
      }).catch(() => { /* ignore */ });
    }
    if (settings.invoice_provider === 'green_invoice') {
      getGreenInvoiceSyncStatus().then(res => {
        if (res.data?.status === 'in_progress') {
          startGreenInvoiceSyncPolling();
        }
      }).catch(() => { /* ignore */ });
    }
    return () => { stopSyncPolling(); stopHeshbonitSyncPolling(); stopGreenInvoiceSyncPolling(); };
  }, [loading, settings.invoice_provider, startSyncPolling, stopSyncPolling, startHeshbonitSyncPolling, stopHeshbonitSyncPolling, startGreenInvoiceSyncPolling, stopGreenInvoiceSyncPolling]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateSettings(settings);
      const savedSettings = res.data?.settings ?? res.data ?? {};
      setSettings({ ...defaultSettings, ...savedSettings });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      window.dispatchEvent(new CustomEvent('settings-changed', { detail: savedSettings }));
    } finally { setSaving(false); }
  };

  const handleRivhitSave = async () => {
    setRivhitSaving(true);
    setRivhitError('');
    setRivhitSaved(false);
    try {
      const payload: { api_token?: string; username?: string; password?: string; id_company?: string; payment_page_token?: string } = {};
      if (rivhitToken) payload.api_token = rivhitToken;
      if (rivhitPaymentPageToken) payload.payment_page_token = rivhitPaymentPageToken;
      if (rivhitUsername) payload.username = rivhitUsername;
      if (rivhitPassword) payload.password = rivhitPassword;
      if (rivhitIdCompany) payload.id_company = rivhitIdCompany;
      const res = await updateRivhitConfig(payload);
      setRivhitHasToken(res.data.has_token ?? false);
      setRivhitHasCredentials(res.data.has_credentials ?? false);
      setRivhitHasPaymentPageToken(res.data.has_payment_page_token ?? false);
      setRivhitToken('');
      setRivhitPaymentPageToken('');
      setRivhitUsername('');
      setRivhitPassword('');
      setRivhitIdCompany('');

      // Start background sync polling if a sync was triggered
      if (res.data.sync_id) {
        startSyncPolling();
      }

      setRivhitSaved(true);
      setTimeout(() => setRivhitSaved(false), 4000);
    } catch {
      setRivhitError(t('settings.rivhit.error'));
    } finally {
      setRivhitSaving(false);
    }
  };

  const handleRivhitTest = async () => {
    setRivhitTesting(true);
    setRivhitError('');
    setRivhitTestResult(null);
    try {
      const payload: { api_token?: string; username?: string; password?: string; id_company?: string; payment_page_token?: string } = {};
      if (rivhitToken) payload.api_token = rivhitToken;
      if (rivhitPaymentPageToken) payload.payment_page_token = rivhitPaymentPageToken;
      if (rivhitUsername) payload.username = rivhitUsername;
      if (rivhitPassword) payload.password = rivhitPassword;
      if (rivhitIdCompany) payload.id_company = rivhitIdCompany;
      const res = await testRivhitConfig(payload);
      setRivhitTestResult({
        success: !!res.data?.success,
        message: res.data?.message || (res.data?.success ? t('settings.rivhit.testSuccess') : t('settings.rivhit.testFailed')),
      });
    } catch (err: any) {
      setRivhitTestResult({
        success: false,
        message: err?.response?.data?.detail || t('settings.rivhit.testFailed'),
      });
    } finally {
      setRivhitTesting(false);
    }
  };

  const handleRivhitResync = async () => {
    setRivhitResyncModalOpen(false);
    setRivhitResyncing(true);
    setRivhitError('');
    setRivhitResyncDone(false);
    try {
      await resyncRivhitData();
      startSyncPolling();
    } catch (err: any) {
      setRivhitError(err?.response?.data?.detail || t('settings.rivhit.resyncError'));
    } finally {
      setRivhitResyncing(false);
    }
  };

  const handleHeshbonitResync = async () => {
    setHeshbonitSyncError('');
    setHeshbonitResyncDone(false);
    setHeshbonitSyncing(true);
    try {
      await resyncHeshbonitData();
      startHeshbonitSyncPolling();
    } catch (err: any) {
      setHeshbonitSyncing(false);
      setHeshbonitSyncError(err?.response?.data?.detail || 'Failed to load data from Heshbonit BeClick');
    }
  };

  const handleHeshbonitSave = async () => {
    setHeshbonitSaving(true);
    setHeshbonitError('');
    setHeshbonitSaved(false);
    try {
      const payload: { username?: string; password?: string } = {};
      if (heshbonitUsername) payload.username = heshbonitUsername;
      if (heshbonitPassword) payload.password = heshbonitPassword;
      const res = await updateHeshbonitConfig(payload);
      setHeshbonitHasCredentials(res.data.has_credentials ?? false);
      setHeshbonitUsername('');
      setHeshbonitPassword('');
      setHeshbonitSaved(true);
      setTimeout(() => setHeshbonitSaved(false), 4000);
    } catch (err: any) {
      setHeshbonitError(err?.response?.data?.detail || 'Failed to save Heshbonit BeClick credentials');
    } finally {
      setHeshbonitSaving(false);
    }
  };

  const handleHeshbonitTest = async () => {
    setHeshbonitTesting(true);
    setHeshbonitError('');
    setHeshbonitTestResult(null);
    try {
      const payload: { username?: string; password?: string } = {};
      if (heshbonitUsername) payload.username = heshbonitUsername;
      if (heshbonitPassword) payload.password = heshbonitPassword;
      const res = await testHeshbonitConfig(payload);
      setHeshbonitTestResult({
        success: !!res.data?.success,
        message: res.data?.message || (res.data?.success ? 'Heshbonit BeClick connection verified' : 'Failed to verify Heshbonit BeClick credentials'),
      });
    } catch (err: any) {
      setHeshbonitTestResult({
        success: false,
        message: err?.response?.data?.detail || 'Failed to verify Heshbonit BeClick credentials',
      });
    } finally {
      setHeshbonitTesting(false);
    }
  };

  const handleGreenInvoiceResync = async () => {
    setGreenInvoiceSyncError('');
    setGreenInvoiceResyncDone(false);
    setGreenInvoiceSyncing(true);
    try {
      await resyncGreenInvoiceData();
      startGreenInvoiceSyncPolling();
    } catch (err: any) {
      setGreenInvoiceSyncing(false);
      setGreenInvoiceSyncError(err?.response?.data?.detail || 'Failed to load data from Green Invoice');
    }
  };

  const handleGreenInvoiceSave = async () => {
    setGreenInvoiceSaving(true);
    setGreenInvoiceError('');
    setGreenInvoiceSaved(false);
    try {
      const payload: { api_key_id?: string; api_key_secret?: string; environment?: string; business_id?: string } = {
        environment: greenInvoiceEnvironment,
      };
      if (greenInvoiceApiKeyId) payload.api_key_id = greenInvoiceApiKeyId;
      if (greenInvoiceApiSecret) payload.api_key_secret = greenInvoiceApiSecret;
      if (greenInvoiceBusinessId) payload.business_id = greenInvoiceBusinessId;
      const res = await updateGreenInvoiceConfig(payload);
      setGreenInvoiceHasCredentials(res.data.has_credentials ?? false);
      setGreenInvoiceEnvironment(res.data.environment ?? greenInvoiceEnvironment);
      setGreenInvoiceApiKeyId('');
      setGreenInvoiceApiSecret('');
      setGreenInvoiceBusinessId('');
      setGreenInvoiceSaved(true);
      setTimeout(() => setGreenInvoiceSaved(false), 4000);
    } catch (err: any) {
      setGreenInvoiceError(err?.response?.data?.detail || 'Failed to save Green Invoice credentials');
    } finally {
      setGreenInvoiceSaving(false);
    }
  };

  const handleGreenInvoiceTest = async () => {
    setGreenInvoiceTesting(true);
    setGreenInvoiceError('');
    setGreenInvoiceTestResult(null);
    try {
      const payload: { api_key_id?: string; api_key_secret?: string; environment?: string; business_id?: string } = {
        environment: greenInvoiceEnvironment,
      };
      if (greenInvoiceApiKeyId) payload.api_key_id = greenInvoiceApiKeyId;
      if (greenInvoiceApiSecret) payload.api_key_secret = greenInvoiceApiSecret;
      if (greenInvoiceBusinessId) payload.business_id = greenInvoiceBusinessId;
      const res = await testGreenInvoiceConfig(payload);
      setGreenInvoiceTestResult({
        success: !!res.data?.success,
        message: res.data?.message || (res.data?.success ? 'Green Invoice connection verified' : 'Failed to verify Green Invoice credentials'),
      });
    } catch (err: any) {
      setGreenInvoiceTestResult({
        success: false,
        message: err?.response?.data?.detail || 'Failed to verify Green Invoice credentials',
      });
    } finally {
      setGreenInvoiceTesting(false);
    }
  };

  const handleSmsSave = async () => {
    if (!smsSettings) return;
    setSmsSaving(true);
    setSmsSaved(false);
    try {
      const { sms_enabled, sms_sender_id, sms_messaging_service_sid, sms_whatsapp_handoff_enabled, default_payment_request_expiration_days } = smsSettings;
      const res = await updateSmsSettings({
        sms_enabled,
        sms_sender_id,
        sms_messaging_service_sid,
        sms_whatsapp_handoff_enabled,
        default_payment_request_expiration_days
      });
      setSmsSettings(res.data);
      // Sync with general settings state
      setSettings(prev => ({
        ...prev,
        sms_payment_notifications_enabled: res.data.sms_enabled,
        sms_sender_id: res.data.sms_sender_id || prev.sms_sender_id,
        sms_whatsapp_handoff_enabled: res.data.sms_whatsapp_handoff_enabled,
        default_payment_request_expiration_days: res.data.default_payment_request_expiration_days,
      }));
      setSmsSaved(true);
      setTimeout(() => setSmsSaved(false), 3000);
    } catch (err: any) {
      console.error('Failed to save SMS settings:', err);
    } finally {
      setSmsSaving(false);
    }
  };

  const paymentTermsRows = useMemo(() => {
    const query = paymentTermsSearch.trim().toLowerCase();
    return customers
      .filter((customer) => {
        if (!query) return customer.payment_terms_days != null;
        const fields = [
          rivhitDisplayName(customer),
          String(customer.customer_id),
          customer.phone,
          customer.email,
          customer.city,
        ].filter(Boolean).join(' ').toLowerCase();
        return fields.includes(query);
      })
      .slice(0, 80);
  }, [customers, paymentTermsSearch]);

  const qrFallbackResults = useMemo(() => {
    const query = qrFallbackSearch.trim().toLowerCase();
    if (query.length < 2) return [];
    return customers
      .filter((customer) => {
        const fields = [
          rivhitDisplayName(customer),
          String(customer.customer_id),
          customer.phone,
          customer.email,
          customer.city,
        ].filter(Boolean).join(' ').toLowerCase();
        return fields.includes(query);
      })
      .slice(0, 8);
  }, [customers, qrFallbackSearch]);

  useEffect(() => {
    if (rivhitHasToken || rivhitHasCredentials) {
      ensureCustomersLoaded();
    }
  }, [rivhitHasToken, rivhitHasCredentials, ensureCustomersLoaded]);

  const handleSyncPaymentTerms = async () => {
    setPaymentTermsSyncing(true);
    setPaymentTermsMessage('');
    setPaymentTermsError('');
    try {
      const res = await syncRivhitCustomerPaymentTerms();
      await refreshCustomers();
      setPaymentTermsMessage(
        t('settings.rivhit.paymentTermsSyncDone', {
          updated: res.data.updated,
          skipped: res.data.skipped,
        }),
      );
    } catch (err: any) {
      setPaymentTermsError(err?.response?.data?.detail || t('settings.rivhit.paymentTermsSyncError'));
    } finally {
      setPaymentTermsSyncing(false);
    }
  };

  const handlePaymentTermsChange = async (customerId: number, value: string) => {
    const days = Math.max(0, Math.min(100, Number(value) || 0));
    setPaymentTermsSavingId(customerId);
    setPaymentTermsMessage('');
    setPaymentTermsError('');
    try {
      await updateRivhitCustomerSettings(customerId, { payment_terms_days: days });
      await refreshCustomers();
      setPaymentTermsMessage(t('settings.rivhit.paymentTermsSaved'));
    } catch (err: any) {
      setPaymentTermsError(err?.response?.data?.detail || t('settings.rivhit.paymentTermsSaveError'));
    } finally {
      setPaymentTermsSavingId(null);
    }
  };

  const reloadPermittedNumbers = async () => {
    const res = await getPermittedNumbers();
    setPermittedNumbers(res.data.numbers || []);
  };

  const handleStartPermittedNumber = async () => {
    setPermittedLoading(true);
    setPermittedMessage('');
    setPermittedError('');
    try {
      const res = await startPermittedNumberVerification({ phone_number: permittedPhone });
      setPermittedNumbers(res.data.numbers || []);
      setPermittedPhone('');
      setPermittedMessage(t('settings.permittedNumbers.verificationSent'));
    } catch (err: any) {
      setPermittedError(err?.response?.data?.detail || t('settings.permittedNumbers.sendError'));
    } finally {
      setPermittedLoading(false);
    }
  };

  const handlePermittedNumberUpdate = async (
    phoneNumber: string,
    updates: { daily_summary?: boolean; notifications_enabled?: boolean },
  ) => {
    setPermittedLoading(true);
    setPermittedMessage('');
    setPermittedError('');
    try {
      const res = await updatePermittedNumber(phoneNumber, updates);
      setPermittedNumbers(res.data.numbers || []);
    } catch (err: any) {
      setPermittedError(err?.response?.data?.detail || t('settings.permittedNumbers.updateError'));
      await reloadPermittedNumbers().catch(() => undefined);
    } finally {
      setPermittedLoading(false);
    }
  };

  const handleDeletePermittedNumber = async (phoneNumber: string) => {
    setPermittedLoading(true);
    setPermittedMessage('');
    setPermittedError('');
    try {
      const res = await deletePermittedNumber(phoneNumber);
      setPermittedNumbers(res.data.numbers || []);
      setPermittedMessage(t('settings.permittedNumbers.removeSuccess'));
    } catch (err: any) {
      setPermittedError(err?.response?.data?.detail || t('settings.permittedNumbers.removeError'));
    } finally {
      setPermittedLoading(false);
    }
  };

  const handleSendBusinessAnalysisNow = async () => {
    setBaSending(true);
    setBaError('');
    setBaMessage('');
    try {
      await sendBusinessAnalysisWhatsAppNow();
      setBaMessage(t('settings.permittedNumbers.testSent'));
    } catch (err: any) {
      setBaError(err?.response?.data?.detail || t('settings.permittedNumbers.testError'));
    } finally {
      setBaSending(false);
    }
  };

  const handleSaveBusinessAnalysisSchedule = async () => {
    setBaLoading(true);
    setBaError('');
    setBaMessage('');
    try {
      const res = await updateBusinessAnalysisSchedule({ send_day: baDay, send_hour: baHour, send_minute: baMinute, timezone: baTimezone });
      setBaDay(res.data.send_day ?? 0);
      setBaHour(res.data.send_hour ?? 8);
      setBaMinute(res.data.send_minute ?? 0);
      setBaTimezone(res.data.timezone ?? 'Asia/Jerusalem');
      setBaMessage(t('settings.permittedNumbers.businessAnalysisScheduleSaved'));
    } catch (err: any) {
      setBaError(err?.response?.data?.detail || t('settings.permittedNumbers.businessAnalysisScheduleError'));
    } finally {
      setBaLoading(false);
    }
  };

  const handleGenerateMcpKey = async () => {
    setMcpKeyLoading(true);
    setMcpKeyError('');
    setMcpGeneratedKey(null);
    setMcpRevokeConfirm(false);
    try {
      const res = await generateMcpKey();
      setMcpGeneratedKey(res.data.key);
      setMcpKeyStatus({ has_key: true, key_prefix: res.data.key_prefix, created_at: res.data.created_at });
    } catch (err: any) {
      setMcpKeyError(err?.response?.data?.detail || 'Failed to generate key');
    } finally {
      setMcpKeyLoading(false);
    }
  };

  const handleRevokeMcpKey = async () => {
    setMcpKeyLoading(true);
    setMcpKeyError('');
    try {
      await revokeMcpKey();
      setMcpKeyStatus({ has_key: false });
      setMcpGeneratedKey(null);
      setMcpRevokeConfirm(false);
    } catch (err: any) {
      setMcpKeyError(err?.response?.data?.detail || 'Failed to revoke key');
    } finally {
      setMcpKeyLoading(false);
    }
  };

  const handleCopyCMcpKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setMcpKeyCopied(true);
      setTimeout(() => setMcpKeyCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleCopyMcpConfig = async (config: string) => {
    try {
      await navigator.clipboard.writeText(config);
      setMcpConfigCopied(true);
      setTimeout(() => setMcpConfigCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleChatUpdate = async (chatId: string, data: Partial<ChatConfig>) => {
    await updateChat(chatId, data);
    setChats((prev) => prev.map((c) => c.id === chatId ? { ...c, ...data } : c));
  };

  const handleRunAnalysis = async (chatId: string) => {
    setRunningAnalysisChatId(chatId);
    try {
      await runOrderSuggestions(chatId);
      await fetchData();
    } catch (err) {
      console.error('Run analysis failed:', err);
    } finally {
      setRunningAnalysisChatId(null);
    }
  };

  const customerBotFieldsDisabled = !settings.customer_bot_enabled;

  if (loading) {
    return <div className="flex items-center justify-center min-h-[40vh] text-gray-400">{t('common.loading')}</div>;
  }

  const settingsTabs = (
    <div className="flex border-b border-gray-200" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={settingsTab === 'configuration'}
        onClick={() => setSettingsTab('configuration')}
        className={`border-b-2 px-4 py-2 text-sm font-medium ${
          settingsTab === 'configuration'
            ? 'border-primary-600 text-primary-700'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        }`}
      >
        {t('settings.configurationTab')}
      </button>
      {canViewUsage && (
        <button
          type="button"
          role="tab"
          aria-selected={settingsTab === 'usage'}
          onClick={() => setSettingsTab('usage')}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            settingsTab === 'usage'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('settings.usageTab')}
        </button>
      )}
    </div>
  );

  if (settingsTab === 'usage' && canViewUsage) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 pb-24">
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        {settingsTabs}
        <UserUsageAnalytics />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24">
      <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
      {settingsTabs}

      {/* ── General ── */}
      <AccordionCard
        icon="🌐"
        title={t('settings.general')}
        description={t('settings.language') + ' · ' + t('settings.businessName', 'Business name') + ' · ' + t('settings.businessHours')}
        defaultOpen
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.businessName', 'Business name')}</label>
          <input
            type="text"
            value={settings.business_name ?? ''}
            onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
            placeholder="Dada Coffee"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          />
          <p className="mt-1 text-xs text-gray-400">{t('settings.businessNameDesc', 'Used in customer-facing payment messages and to generate the default SMS sender ID.')}</p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">{t('settings.bankTransferDetails', 'Bank transfer details')}</label>
          <textarea
            value={settings.bank_transfer_details ?? ''}
            onChange={(event) => setSettings({ ...settings, bank_transfer_details: event.target.value })}
            maxLength={1000}
            rows={3}
            placeholder={t('settings.bankTransferDetailsPlaceholder', 'Bank, branch, account number, and beneficiary name')}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            data-testid="bank-transfer-details-input"
          />
          <p className="mt-1 text-xs text-gray-400">{t('settings.bankTransferDetailsDesc', 'Included in the Send Debt Report message when configured.')}</p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">{t('settings.timezone')}</label>
          <input
            type="text"
            list="workspace-timezones"
            value={settings.timezone}
            onChange={(event) => setSettings({ ...settings, timezone: event.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          />
          <datalist id="workspace-timezones">
            <option value="Asia/Jerusalem" />
            <option value="UTC" />
            <option value="Europe/London" />
            <option value="America/New_York" />
            <option value="America/Los_Angeles" />
          </datalist>
          <p className="mt-1 text-xs text-gray-400">{t('settings.timezoneDesc')}</p>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">{t('settings.language')}</label>
          <LanguageSwitcher />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.businessHours')}</label>
          <div className="flex items-center gap-3">
            <input type="time" value={settings.business_hours_start} onChange={(e) => setSettings({ ...settings, business_hours_start: e.target.value })} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
            <span className="text-gray-400">—</span>
            <input type="time" value={settings.business_hours_end} onChange={(e) => setSettings({ ...settings, business_hours_end: e.target.value })} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
          </div>
        </div>
      </AccordionCard>

      {/* ── Workspace Members ── */}
      <AccordionCard
        icon="👤"
        title={t('settings.workspaceAccess', 'Workspace access')}
        description={t('settings.workspaceAccessDesc', 'Invite Gmail users and configure Account Executive customer access')}
      >
        {!canManageMembers && (
          <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-500">
            {t('settings.workspaceOwnerOnly', 'Only the workspace owner can invite members and change roles.')}
          </p>
        )}

        {canInviteAndSetRole && (
          <div className="grid gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder={t('settings.inviteNamePlaceholder', 'Full name (required)')}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('settings.inviteEmailPlaceholder', 'Email — for workspace login (optional)')}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="member">Member</option>
                <option value="account_executive">Account Executive</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="button"
                onClick={handleInviteMember}
                disabled={inviteSaving || (!inviteName.trim() && !inviteEmail.trim())}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {inviteSaving ? 'Adding...' : 'Add'}
              </button>
            </div>
            <input
              type="tel"
              value={invitePhone}
              onChange={(e) => setInvitePhone(e.target.value)}
              placeholder="Phone — for WhatsApp/SMS messages (optional) e.g. +972501234567"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
            <p className="text-xs text-gray-400">
              Name is required. Email lets the member log in to the workspace. Phone forwards customer messages via WhatsApp/SMS.
            </p>
          </div>
        )}

        {memberMessage && <p className="text-sm font-medium text-green-700">{memberMessage}</p>}
        {memberError && <p className="text-sm font-medium text-red-600">{memberError}</p>}

        <div className="space-y-2">
          {members.length === 0 ? (
            <p className="text-sm text-gray-500">No workspace members found.</p>
          ) : (
            members.map((member) => {
              const isAE = member.role === 'account_executive';
              const isOwner = member.role === 'owner';
              const isEditing = editingMemberId === member.user_id;
              const isExpanded = expandedMemberIds.has(member.user_id);
              const hasDetails = isAE || !!member.phone_number || canManageMembers;
              const toggleExpand = () => {
                setExpandedMemberIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(member.user_id)) next.delete(member.user_id);
                  else next.add(member.user_id);
                  return next;
                });
              };
              const roleBadgeClass = isOwner
                ? 'bg-purple-100 text-purple-700'
                : isAE
                  ? 'bg-blue-100 text-blue-700'
                  : member.role === 'admin'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600';
              const statusBadgeClass = member.status === 'active'
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700';

              return (
                <div key={member.user_id} className="rounded-md border border-gray-200 overflow-hidden">
                  {/* ── Collapsed header row ── */}
                  <div
                    className={`flex items-center gap-2 px-3 py-2.5 ${hasDetails ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    onClick={hasDetails ? toggleExpand : undefined}
                  >
                    {/* Chevron */}
                    {hasDetails ? (
                      <svg
                        className={`shrink-0 h-3.5 w-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        viewBox="0 0 20 20" fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <span className="shrink-0 w-3.5" />
                    )}

                    {/* Name */}
                    <span className="font-medium text-gray-900 text-sm min-w-0 truncate">
                      {member.name || member.email || member.user_id}
                    </span>

                    {/* Email */}
                    <span className="hidden sm:block text-xs text-gray-400 min-w-0 truncate flex-1">
                      {member.email || <span className="italic">no email</span>}
                    </span>

                    {/* Role badge */}
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${roleBadgeClass}`}>
                      {member.role === 'account_executive' ? 'AE' : member.role}
                    </span>

                    {/* Status badge */}
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusBadgeClass}`}>
                      {member.status}
                    </span>

                    {/* Phone + WA status */}
                    {member.phone_number && (
                      <span className="hidden md:flex shrink-0 items-center gap-1 text-xs text-gray-500">
                        📱 {member.phone_number}
                        {member.whatsapp_status && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            member.whatsapp_status === 'verified'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {member.whatsapp_status === 'verified' ? '✓ WA' : '⏳ WA'}
                          </span>
                        )}
                      </span>
                    )}

                    {/* WA quick-action buttons (always visible for pending) */}
                    {member.phone_number && member.whatsapp_status === 'pending' && canManageMembers && (
                      <div className="shrink-0 flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          title="Resend WhatsApp verification message"
                          disabled={waActionLoading !== null}
                          onClick={() => handleResendWaApproval(member.phone_number!)}
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-50"
                        >
                          {waActionLoading === `resend:${member.phone_number}` ? '…' : 'Resend'}
                        </button>
                        <button
                          type="button"
                          title="Force approve without sending a message"
                          disabled={waActionLoading !== null}
                          onClick={() => handleForceWaApproval(member.phone_number!)}
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 disabled:opacity-50"
                        >
                          {waActionLoading === `force:${member.phone_number}` ? '…' : 'Force ✓'}
                        </button>
                      </div>
                    )}

                    {/* Edit / Remove (always visible on row) */}
                    {!isOwner && canManageMembers && !isEditing && (
                      <div className="shrink-0 flex gap-1.5 ml-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingMemberId(member.user_id);
                            setExpandedMemberIds((prev) => new Set([...prev, member.user_id]));
                          }}
                          className="rounded border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        {canInviteAndSetRole && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member)}
                            className="rounded border border-red-300 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    {isOwner && (
                      <span className="shrink-0 ml-auto text-xs text-gray-400 italic">Owner</span>
                    )}
                  </div>

                  {/* ── Expanded details ── */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 space-y-3">
                      {/* Email (shown on mobile since it's hidden in header) */}
                      <div className="sm:hidden text-xs text-gray-500">
                        {member.email || <span className="italic text-gray-400">no email</span>}
                      </div>

                      {/* Phone + WA status (shown on small screens) */}
                      {member.phone_number && (
                        <div className="md:hidden text-xs text-gray-500 flex flex-wrap items-center gap-1">
                          📱 {member.phone_number}
                          {member.whatsapp_status && (
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                              member.whatsapp_status === 'verified'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {member.whatsapp_status === 'verified' ? '✓ WA' : '⏳ WA'}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Edit panel */}
                      {isEditing && (
                        <MemberEditPanel
                          member={member}
                          canSetRole={canInviteAndSetRole}
                          onSave={(updates) => {
                            if (Object.keys(updates).length > 0) {
                              const losingAssignments =
                                member.role === 'account_executive' &&
                                updates.role &&
                                updates.role !== 'account_executive' &&
                                (member.assigned_customer_ids || []).length > 0;
                              if (
                                losingAssignments &&
                                !window.confirm(
                                  `Changing ${member.name || member.email || 'this member'}'s role from Account Executive will remove all their customer assignments (${member.assigned_customer_ids!.length} customers). Continue?`,
                                )
                              ) {
                                setEditingMemberId(null);
                                return;
                              }
                              handleMemberUpdate(member, updates);
                            }
                            setEditingMemberId(null);
                          }}
                          onCancel={() => setEditingMemberId(null)}
                        />
                      )}

                      {/* AE settings */}
                      {isAE && (
                        <div className="grid gap-3 md:grid-cols-2">
                          {member.status === 'pending' && (
                            <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                              <p className="text-amber-900">{t('settings.aeApprovalHint')}</p>
                              {canInviteAndSetRole ? (
                                <button type="button" disabled={aeApprovalLoading !== null || !member.phone_number}
                                  onClick={() => handleApproveAeNotifications(member)}
                                  data-testid={`approve-ae-notifications-${member.user_id}`}
                                  className="mt-2 rounded-md bg-primary-600 px-3 py-2 font-medium text-white disabled:opacity-50">
                                  {aeApprovalLoading === member.user_id ? t('settings.aeApproving') : t('settings.approveAeNotifications')}
                                </button>
                              ) : <p className="mt-1 text-xs text-amber-800">{t('settings.aeApprovalOwnerOnly')}</p>}
                            </div>
                          )}
                          <label className="block text-sm">
                            <span className="mb-1 block font-medium text-gray-700">Query access</span>
                            <select
                              value={member.ae_query_scope || 'assigned'}
                              disabled={!canManageMembers}
                              onChange={(e) => handleMemberUpdate(member, { ae_query_scope: e.target.value as 'assigned' | 'all' })}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
                            >
                              <option value="assigned">Assigned customers only</option>
                              <option value="all">All customers</option>
                            </select>
                          </label>

                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 md:self-end">
                            <input
                              type="checkbox"
                              checked={member.ae_whatsapp_notifications_enabled !== false}
                              disabled={!canManageMembers}
                              onChange={(e) => handleMemberUpdate(member, { ae_whatsapp_notifications_enabled: e.target.checked })}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600"
                            />
                            WhatsApp customer notifications
                          </label>

                          <p className="md:col-span-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                            💡 Assign customers to this AE from the{' '}
                            <a href="/customers" className="underline hover:text-blue-800">Customers page</a>.
                          </p>
                        </div>
                      )}

                      <div className="rounded-md border border-gray-200 bg-white p-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <input type="checkbox"
                            checked={member.customer_reminder_notifications_enabled ?? (isAE && member.ae_whatsapp_notifications_enabled !== false)}
                            disabled={!canManageMembers || reminderPreferenceSaving !== null}
                            onChange={async (e) => {
                              const enabled = e.target.checked;
                              setReminderPreferenceSaving(member.user_id);
                              try { await handleMemberUpdate(member, { customer_reminder_notifications_enabled: enabled }); }
                              finally { setReminderPreferenceSaving(null); }
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600" />
                          {t('settings.customerReminderNotifications')}
                        </label>
                        <p className="mt-1 text-xs text-gray-500">{t('settings.customerReminderNotificationsHint')}</p>
                      </div>

                      {/* WhatsApp notification settings */}
                      {member.phone_number && (() => {
                        const pn = permittedNumbers.find((n) => n.phone_number === member.phone_number);
                        if (!pn) return (
                          <p className="text-xs text-amber-600">
                            ⏳ Phone is being registered as a permitted WhatsApp number — check back shortly.
                          </p>
                        );
                        const verified = pn.status === 'verified';
                        return (
                          <div>
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">WhatsApp notification settings</p>
                            <div className="grid gap-2 sm:grid-cols-2">
                              <label className="flex items-center justify-between gap-3 rounded-md bg-white border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700">
                                <span>Daily summary</span>
                                <input
                                  type="checkbox"
                                  checked={pn.daily_summary}
                                  disabled={permittedLoading || !verified}
                                  onChange={(e) => handlePermittedNumberUpdate(member.phone_number!, { daily_summary: e.target.checked })}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                                />
                              </label>
                              <label className="flex items-center justify-between gap-3 rounded-md bg-white border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700">
                                <span>General notifications</span>
                                <input
                                  type="checkbox"
                                  checked={pn.notifications_enabled !== false}
                                  disabled={permittedLoading || !verified}
                                  onChange={(e) => handlePermittedNumberUpdate(member.phone_number!, { notifications_enabled: e.target.checked })}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                                />
                              </label>
                            </div>
                            {isAE && (
                              <p className="mt-1 text-xs text-gray-400">
                                📍 Customer notifications sent to this number are scoped to their assigned customers.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── Additional (standalone) numbers & add-number form ── */}
        {(() => {
          const memberPhones = new Set(members.map((m) => m.phone_number).filter(Boolean));
          const standaloneNumbers = permittedNumbers.filter((n) => !memberPhones.has(n.phone_number));
          return (
            <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Additional WhatsApp numbers</h4>
              <p className="text-xs text-gray-500">Add phone numbers that should receive notifications but are not linked to a workspace member.</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="tel"
                  value={permittedPhone}
                  onChange={(e) => setPermittedPhone(e.target.value)}
                  placeholder="+972501234567"
                  className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
                <button
                  type="button"
                  onClick={handleStartPermittedNumber}
                  disabled={permittedLoading || !permittedPhone.trim()}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {permittedLoading ? t('settings.permittedNumbers.sending') : t('settings.permittedNumbers.add')}
                </button>
              </div>

              {permittedMessage && <p className="text-sm font-medium text-green-700">✓ {permittedMessage}</p>}
              {permittedError && <p className="text-sm font-medium text-red-600">✕ {permittedError}</p>}

              {standaloneNumbers.length > 0 && (
                <div className="space-y-2">
                  {standaloneNumbers.map((number) => {
                    const verified = number.status === 'verified';
                    return (
                      <div key={number.phone_number} className="rounded-md border border-gray-200 px-3 py-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{number.phone_number}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                              <span className="text-gray-400 italic">No linked member</span>
                              <span className={`rounded-full px-2 py-0.5 font-medium ${
                                verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {verified ? t('settings.permittedNumbers.verified') : t('settings.permittedNumbers.pending')}
                              </span>
                              {number.last_error && <span className="text-red-600">{number.last_error}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeletePermittedNumber(number.phone_number)}
                            disabled={permittedLoading}
                            className="self-start rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 sm:self-auto"
                          >
                            {t('common.remove', 'Remove')}
                          </button>
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <label className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700">
                            <span>{t('settings.permittedNumbers.dailySummaryToggle')}</span>
                            <input
                              type="checkbox"
                              checked={number.daily_summary}
                              disabled={permittedLoading || !verified}
                              onChange={(e) => handlePermittedNumberUpdate(number.phone_number, { daily_summary: e.target.checked })}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600"
                            />
                          </label>
                          <label className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700">
                            <span>{t('settings.permittedNumbers.notificationsToggle')}</span>
                            <input
                              type="checkbox"
                              checked={number.notifications_enabled !== false}
                              disabled={permittedLoading || !verified}
                              onChange={(e) => handlePermittedNumberUpdate(number.phone_number, { notifications_enabled: e.target.checked })}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600"
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </AccordionCard>

      {/* ── Collections Module ── */}
      <AccordionCard
        icon="₪"
        title={t('modules.collections.name', 'Collections')}
        description={t('modules.collections.description', 'Debt thresholds and collection settings')}
        moduleId="collections"
      >
        <div
          className="mb-4 flex items-center justify-between gap-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
          data-testid="hourly-invoice-debt-check-row"
        >
          <div>
            <label className="text-sm font-medium text-gray-700">
              {t('settings.hourlyInvoiceDebtCheck')}
            </label>
            <p className="text-xs text-gray-400">
              {t('settings.hourlyInvoiceDebtCheckDesc')}
            </p>
          </div>
          <ToggleSwitch
            checked={settings.collections?.hourly_invoice_debt_check_enabled === true}
            onChange={(val) => setSettings({
              ...settings,
              collections: {
                ...settings.collections,
                hourly_invoice_debt_check_enabled: val,
              },
            })}
            size="md"
            aria-label={t('settings.hourlyInvoiceDebtCheck')}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.debtAgeThreshold')}</label>
            <input
              type="number"
              min={1}
              value={settings.collections?.debt_age_threshold_days}
              onChange={(e) => setSettings({
                ...settings,
                collections: {
                  ...settings.collections,
                  debt_age_threshold_days: Math.max(1, Number(e.target.value) || 1)
                }
              })}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-gray-400">{t('settings.debtAgeThresholdDesc')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.invoiceBlockDebtAgeThreshold')}</label>
            <input
              type="number"
              min={1}
              value={settings.collections?.invoice_block_debt_age_days}
              onChange={(e) => setSettings({
                ...settings,
                collections: {
                  ...settings.collections,
                  invoice_block_debt_age_days: Math.max(1, Number(e.target.value) || 1)
                }
              })}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-gray-400">{t('settings.invoiceBlockDebtAgeThresholdDesc')}</p>
          </div>
        </div>

        {/* ── QR walk-in fallback (go-to) customer ── */}
        <div className="space-y-2 border-t border-gray-100 pt-4 mt-4">
          <p className="text-sm font-semibold text-gray-700">{t('settings.qrFallback.title', 'Walk-in (QR) fallback customer')}</p>
          <p className="text-xs text-gray-500">{t('settings.qrFallback.description', "Used when a QR payer's phone matches more than one customer and can't be uniquely identified. The payment and receipt are attributed to this go-to customer. Set one before sharing QR codes with walk-in customers.")}</p>

          {settings.qr_fallback_customer_id ? (
            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-gray-800">{settings.qr_fallback_customer_name || t('settings.qrFallback.selected', 'Selected customer')}</div>
                <div className="text-xs text-gray-400">#{settings.qr_fallback_customer_id}</div>
              </div>
              <button
                type="button"
                onClick={() => setSettings({ ...settings, qr_fallback_customer_id: '', qr_fallback_customer_name: '' })}
                className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                {t('settings.qrFallback.clear', 'Clear')}
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={qrFallbackSearch}
                onFocus={() => ensureCustomersLoaded()}
                onChange={(e) => setQrFallbackSearch(e.target.value)}
                placeholder={t('settings.qrFallback.searchPlaceholder', 'Search customer by name')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
              {qrFallbackResults.length > 0 && (
                <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm">
                  {qrFallbackResults.map((customer) => (
                    <button
                      type="button"
                      key={customer.customer_id}
                      onClick={() => {
                        setSettings({
                          ...settings,
                          qr_fallback_customer_id: String(customer.customer_id),
                          qr_fallback_customer_name: rivhitDisplayName(customer),
                        });
                        setQrFallbackSearch('');
                      }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      <span className="text-gray-800">{rivhitDisplayName(customer)}</span>
                      <span className="text-xs text-gray-400">#{customer.customer_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {(rivhitHasToken || rivhitHasCredentials) && (
          <div className="space-y-3 border-t border-gray-100 pt-4 mt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-700">{t('settings.rivhit.paymentTermsTitle')}</p>
                <p className="text-xs text-gray-500">{t('settings.rivhit.paymentTermsDescription')}</p>
              </div>
              <button
                onClick={handleSyncPaymentTerms}
                disabled={paymentTermsSyncing || customersLoading}
                className="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800 shadow-sm hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                {paymentTermsSyncing ? t('settings.rivhit.paymentTermsSyncing') : t('settings.rivhit.paymentTermsSync')}
              </button>
            </div>

            <input
              type="text"
              value={paymentTermsSearch}
              onChange={(e) => setPaymentTermsSearch(e.target.value)}
              placeholder={t('settings.rivhit.paymentTermsSearch')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />

            {paymentTermsMessage && <p className="text-sm font-medium text-green-700">{paymentTermsMessage}</p>}
            {paymentTermsError && <p className="text-sm font-medium text-red-600">{paymentTermsError}</p>}

            <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200">
              {customersLoading ? (
                <p className="px-3 py-4 text-sm text-gray-500">{t('settings.rivhit.paymentTermsLoading')}</p>
              ) : paymentTermsRows.length === 0 ? (
                <p className="px-3 py-4 text-sm text-gray-500">{t('settings.rivhit.paymentTermsEmpty')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">{t('settings.rivhit.paymentTermsCustomer')}</th>
                      <th className="px-3 py-2">{t('settings.rivhit.paymentTermsDays')}</th>
                      <th className="px-3 py-2">{t('settings.rivhit.paymentTermsSource')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paymentTermsRows.map((customer) => (
                      <tr key={customer.customer_id}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{rivhitDisplayName(customer)}</div>
                          <div className="text-xs text-gray-400">#{customer.customer_id}</div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={customer.payment_terms_days ?? ''}
                            onChange={(e) => handlePaymentTermsChange(customer.customer_id, e.target.value)}
                            disabled={paymentTermsSavingId === customer.customer_id}
                            className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm"
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {customer.payment_terms_source || t('settings.rivhit.paymentTermsNotSet')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </AccordionCard>

      {/* ── Integrations ── */}
      <AccordionCard
        icon="🔌"
        title={t('settings.integrations.title', 'Integrations')}
        description={t('settings.integrations.description', 'Accounting and payment service connections')}
      >
        <div className="border-b border-gray-100 pb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Invoice provider</label>
          <select
            value={settings.invoice_provider}
            onChange={(e) => {
              const nextProvider = e.target.value as 'rivhit' | 'heshbonit_beclick' | 'green_invoice';
              setSettings({
                ...settings,
                invoice_provider: nextProvider,
                payment_links_enabled: nextProvider === 'heshbonit_beclick' ? false : settings.payment_links_enabled,
              });
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="rivhit">Rivhit</option>
            <option value="heshbonit_beclick">Heshbonit BeClick</option>
            <option value="green_invoice">Green Invoice</option>
          </select>
          <p className="mt-1 text-xs text-gray-400">
            One accounting provider is active per workspace. Payment links can use the configured credit-card provider.
          </p>
        </div>

        {/* ── Accounting (Rivhit) sub-section ── */}
        {settings.invoice_provider === 'rivhit' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">🏦 {t('settings.integrations.accounting', 'Accounting')} — Rivhit</h3>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
              rivhitHasToken || rivhitHasCredentials
                ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${rivhitHasToken || rivhitHasCredentials ? 'bg-green-500' : 'bg-gray-400'}`} />
              {rivhitHasToken || rivhitHasCredentials ? t('settings.rivhit.connected') : t('settings.rivhit.notConfigured')}
            </span>
          </div>


          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.username')}</label>
              <input type="text" value={rivhitUsername} onChange={(e) => setRivhitUsername(e.target.value)} placeholder={rivhitHasCredentials ? '••••••••' : t('settings.rivhit.usernamePlaceholder')} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.password')}</label>
              <input type="password" value={rivhitPassword} onChange={(e) => setRivhitPassword(e.target.value)} placeholder={rivhitHasCredentials ? '••••••••' : t('settings.rivhit.passwordPlaceholder')} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.idCompany')}</label>
              <input type="text" value={rivhitIdCompany} onChange={(e) => setRivhitIdCompany(e.target.value)} placeholder={rivhitHasCredentials ? '••••••••' : t('settings.rivhit.idCompanyPlaceholder')} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            {rivhitHasCredentials && !rivhitUsername && !rivhitPassword && !rivhitIdCompany && (
              <span className="text-xs text-green-600">✓ {t('settings.rivhit.credentialsSet')}</span>
            )}
            <p className="text-xs text-gray-400">🔒 {t('settings.rivhit.credentialsHint')}</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.apiToken')}</label>
              <div className="relative">
                <input type="password" value={rivhitToken} onChange={(e) => setRivhitToken(e.target.value)} placeholder={rivhitHasToken ? '••••••••••••••••' : t('settings.rivhit.apiTokenPlaceholder')} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
                {rivhitHasToken && !rivhitToken && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-600">✓ {t('settings.rivhit.tokenSet')}</span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">🔒 {t('settings.rivhit.apiTokenHint')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button onClick={handleRivhitTest} disabled={rivhitTesting || rivhitSaving || (!rivhitToken && !rivhitHasToken && !rivhitUsername && !rivhitPassword && !rivhitIdCompany && !rivhitHasCredentials)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors">
              {rivhitTesting ? t('settings.rivhit.testing') : t('settings.rivhit.test')}
            </button>
            <button onClick={handleRivhitSave} disabled={rivhitSaving || (!rivhitToken && !rivhitHasToken && !rivhitUsername && !rivhitHasCredentials && !rivhitPaymentPageToken && !rivhitHasPaymentPageToken)} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {rivhitSaving ? t('settings.rivhit.saving') : t('settings.rivhit.save')}
            </button>
            {rivhitSaved && <span className="text-sm text-green-600 font-medium">✓ {t('settings.rivhit.saved')}</span>}
            {rivhitResyncDone && <span className="text-sm text-green-600 font-medium">✓ {t('settings.rivhit.resyncDone')}</span>}
            {rivhitTestResult && (
              <span className={`text-sm font-medium ${rivhitTestResult.success ? 'text-green-600' : 'text-amber-700'}`}>
                {rivhitTestResult.success ? '✓' : '!'} {rivhitTestResult.message}
              </span>
            )}
            {rivhitError && <span className="text-sm text-red-600 font-medium">✕ {rivhitError}</span>}
          </div>

          {(rivhitHasToken || rivhitHasCredentials) && (
            <div className="flex items-center gap-3 border-t border-gray-100 pt-4 mt-4">
              {rivhitSyncing || rivhitResyncing ? (
                <div className="flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-2">
                  <svg className="h-4 w-4 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-700">{t('settings.rivhit.syncInProgress', 'Syncing data from Rivhit…')}</span>
                </div>
              ) : (
                <button onClick={() => setRivhitResyncModalOpen(true)} disabled={rivhitSaving} className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-100 disabled:opacity-50 transition-colors">
                  {t('settings.rivhit.resync')}
                </button>
              )}
              {!rivhitSyncing && !rivhitResyncing && (
                <span className="text-xs text-gray-500">{t('settings.rivhit.resyncHint')}</span>
              )}
            </div>
          )}
        </div>
        )}

        {settings.invoice_provider === 'green_invoice' && (
        <div className="border-t border-gray-100 pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">🏦 Accounting — Green Invoice</h3>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
              greenInvoiceHasCredentials
                ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${greenInvoiceHasCredentials ? 'bg-green-500' : 'bg-gray-400'}`} />
              {greenInvoiceHasCredentials ? t('settings.rivhit.connected') : t('settings.rivhit.notConfigured')}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select value={greenInvoiceEnvironment} onChange={(e) => setGreenInvoiceEnvironment(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none">
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API key id</label>
              <input type="text" value={greenInvoiceApiKeyId} onChange={(e) => setGreenInvoiceApiKeyId(e.target.value)} placeholder={greenInvoiceHasCredentials ? '••••••••' : 'Enter Green Invoice API key id'} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API key secret</label>
              <input type="password" value={greenInvoiceApiSecret} onChange={(e) => setGreenInvoiceApiSecret(e.target.value)} placeholder={greenInvoiceHasCredentials ? '••••••••' : 'Enter Green Invoice API key secret'} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business id (optional)</label>
              <input type="text" value={greenInvoiceBusinessId} onChange={(e) => setGreenInvoiceBusinessId(e.target.value)} placeholder="Optional Green Invoice business id" className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            {greenInvoiceHasCredentials && !greenInvoiceApiKeyId && !greenInvoiceApiSecret && (
              <span className="text-xs text-green-600">✓ Credentials saved</span>
            )}
            <p className="text-xs text-gray-400">Green Invoice API access requires a Morning Best plan or higher. Sandbox and production credentials are separate.</p>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button onClick={handleGreenInvoiceTest} disabled={greenInvoiceTesting || greenInvoiceSaving || (!greenInvoiceApiKeyId && !greenInvoiceApiSecret && !greenInvoiceHasCredentials)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors">
              {greenInvoiceTesting ? 'Testing...' : 'Test'}
            </button>
            <button onClick={handleGreenInvoiceSave} disabled={greenInvoiceSaving || (!greenInvoiceApiKeyId && !greenInvoiceHasCredentials)} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {greenInvoiceSaving ? 'Saving...' : 'Save'}
            </button>
            {greenInvoiceSaved && <span className="text-sm text-green-600 font-medium">✓ Saved</span>}
            {greenInvoiceTestResult && (
              <span className={`text-sm font-medium ${greenInvoiceTestResult.success ? 'text-green-600' : 'text-amber-700'}`}>
                {greenInvoiceTestResult.success ? '✓' : '!'} {greenInvoiceTestResult.message}
              </span>
            )}
            {greenInvoiceError && <span className="text-sm text-red-600 font-medium">✕ {greenInvoiceError}</span>}
          </div>

          {greenInvoiceHasCredentials && (
            <div className="flex items-center gap-3 border-t border-gray-100 pt-4 mt-4">
              {greenInvoiceSyncing ? (
                <div className="flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-2">
                  <svg className="h-4 w-4 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-700">Loading data from Green Invoice…</span>
                </div>
              ) : (
                <button onClick={handleGreenInvoiceResync} disabled={greenInvoiceSaving} className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-100 disabled:opacity-50 transition-colors">
                  Load data
                </button>
              )}
              {!greenInvoiceSyncing && (
                <span className="text-xs text-gray-500">Pull invoices, receipts and customers from Green Invoice into the app.</span>
              )}
              {greenInvoiceResyncDone && <span className="text-sm text-green-600 font-medium">✓ Data loaded</span>}
              {greenInvoiceSyncError && <span className="text-sm text-red-600 font-medium">✕ {greenInvoiceSyncError}</span>}
            </div>
          )}
        </div>
        )}

        {settings.invoice_provider === 'heshbonit_beclick' && (
        <div className="border-t border-gray-100 pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">🏦 Accounting — Heshbonit BeClick</h3>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
              heshbonitHasCredentials
                ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${heshbonitHasCredentials ? 'bg-green-500' : 'bg-gray-400'}`} />
              {heshbonitHasCredentials ? t('settings.rivhit.connected') : t('settings.rivhit.notConfigured')}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input type="text" value={heshbonitUsername} onChange={(e) => setHeshbonitUsername(e.target.value)} placeholder={heshbonitHasCredentials ? '••••••••' : 'Enter BeClick username'} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" value={heshbonitPassword} onChange={(e) => setHeshbonitPassword(e.target.value)} placeholder={heshbonitHasCredentials ? '••••••••' : 'Enter BeClick password'} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
            </div>
            {heshbonitHasCredentials && !heshbonitUsername && !heshbonitPassword && (
              <span className="text-xs text-green-600">✓ Credentials saved</span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button onClick={handleHeshbonitTest} disabled={heshbonitTesting || heshbonitSaving || (!heshbonitUsername && !heshbonitPassword && !heshbonitHasCredentials)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 transition-colors">
              {heshbonitTesting ? 'Testing...' : 'Test'}
            </button>
            <button onClick={handleHeshbonitSave} disabled={heshbonitSaving || (!heshbonitUsername && !heshbonitHasCredentials)} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {heshbonitSaving ? 'Saving...' : 'Save'}
            </button>
            {heshbonitSaved && <span className="text-sm text-green-600 font-medium">✓ Saved</span>}
            {heshbonitTestResult && (
              <span className={`text-sm font-medium ${heshbonitTestResult.success ? 'text-green-600' : 'text-amber-700'}`}>
                {heshbonitTestResult.success ? '✓' : '!'} {heshbonitTestResult.message}
              </span>
            )}
            {heshbonitError && <span className="text-sm text-red-600 font-medium">✕ {heshbonitError}</span>}
          </div>

          {heshbonitHasCredentials && (
            <div className="flex items-center gap-3 border-t border-gray-100 pt-4 mt-4">
              {heshbonitSyncing ? (
                <div className="flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-2">
                  <svg className="h-4 w-4 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-700">Loading data from Heshbonit BeClick…</span>
                </div>
              ) : (
                <button onClick={handleHeshbonitResync} disabled={heshbonitSaving} className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-100 disabled:opacity-50 transition-colors">
                  Load data
                </button>
              )}
              {!heshbonitSyncing && (
                <span className="text-xs text-gray-500">Pull invoices, receipts and customers from Heshbonit BeClick into the app.</span>
              )}
              {heshbonitResyncDone && <span className="text-sm text-green-600 font-medium">✓ Data loaded</span>}
              {heshbonitSyncError && <span className="text-sm text-red-600 font-medium">✕ {heshbonitSyncError}</span>}
            </div>
          )}
        </div>
        )}

        {/* ── Automations (provider-independent) ── */}
        <div className="border-t border-gray-100 pt-4 mt-4">
          <div className="flex items-center justify-between" data-testid="automations-enabled-row">
            <div>
              <label className="text-sm font-medium text-gray-700">{t('settings.automationsEnabled')}</label>
              <p className="text-xs text-gray-400">{t('settings.automationsEnabledDesc')}</p>
            </div>
            <ToggleSwitch
              checked={settings.automations_enabled !== false}
              onChange={(val) => setSettings({ ...settings, automations_enabled: val })}
              size="md"
            />
          </div>
        </div>

        {/* ── Payment sub-section ── */}
        {(settings.invoice_provider === 'rivhit' || settings.invoice_provider === 'green_invoice') && (
        <div className="border-t border-gray-100 pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">💳 {t('settings.integrations.payment', 'Payment')}</h3>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
              (rivhitHasPaymentPageToken || greenInvoiceHasCredentials) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${(rivhitHasPaymentPageToken || greenInvoiceHasCredentials) ? 'bg-green-500' : 'bg-gray-400'}`} />
              {(rivhitHasPaymentPageToken || greenInvoiceHasCredentials) ? t('settings.rivhit.connected') : t('settings.rivhit.notConfigured')}
            </span>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">{t('settings.paymentLinks')}</label>
              <p className="text-xs text-gray-400">{t('settings.paymentLinksDesc')}</p>
            </div>
            <ToggleSwitch
              checked={settings.payment_links_enabled}
              onChange={(val) => setSettings({ ...settings, payment_links_enabled: val })}
              size="md"
            />
          </div>


          {settings.invoice_provider === 'rivhit' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.rivhit.paymentPageToken')}</label>
              <div className="relative">
                <input type="password" value={rivhitPaymentPageToken} onChange={(e) => setRivhitPaymentPageToken(e.target.value)} placeholder={rivhitHasPaymentPageToken ? '••••••••••••••••' : t('settings.rivhit.paymentPageTokenPlaceholder')} className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none" />
                {rivhitHasPaymentPageToken && !rivhitPaymentPageToken && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-600">✓ {t('settings.rivhit.paymentPageTokenSet')}</span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('settings.rivhit.paymentPageTokenHint')}</p>
            </div>

            <div className="flex items-center gap-3 mt-4 mb-6">
              <button onClick={handleRivhitSave} disabled={rivhitSaving || (!rivhitPaymentPageToken && !rivhitHasPaymentPageToken)} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors">
                {rivhitSaving ? t('settings.rivhit.saving') : t('settings.rivhit.save')}
              </button>
              {rivhitSaved && <span className="text-sm text-green-600 font-medium">✓ {t('settings.rivhit.saved')}</span>}
              {rivhitError && <span className="text-sm text-red-600 font-medium">✕ {rivhitError}</span>}
            </div>
          </>
          )}

          {featureFlags.standalonePaymentLinks && (
            <div className="border-t border-gray-100 pt-4">
              <PaymentLinkSettings />
            </div>
          )}

        </div>
        )}
        {settings.invoice_provider === 'heshbonit_beclick' && (
          <div className="border-t border-gray-100 pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              💳 {t('settings.integrations.payment', 'Payment')} — pELECARD
            </h3>
            <PaymentLinkSettings />
          </div>
        )}
      </AccordionCard>

      {/* ── SMS Notifications ── */}
      <AccordionCard
        icon="📱"
        title={t('settings.sms.title', 'SMS Notifications')}
        description={t('settings.sms.description', 'Enable and configure SMS payment notifications')}
      >
        {!smsSettings ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('settings.sms.enabled', 'Enable SMS Notifications')}</label>
                <p className="text-xs text-gray-400">{t('settings.sms.enabledDesc', 'Send payment requests via SMS when WhatsApp is unavailable or as a fallback.')}</p>
              </div>
              <ToggleSwitch
                checked={smsSettings.sms_enabled}
                onChange={(val) => {
                  setSmsSettings({ ...smsSettings, sms_enabled: val });
                  setSettings({ ...settings, sms_payment_notifications_enabled: val });
                }}
                size="md"
              />
            </div>

            <div className={`space-y-4 transition-opacity ${!smsSettings.sms_enabled ? 'opacity-60' : ''}`}>
              {!smsSettings.sms_enabled && (
                <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                  {t('settings.sms.disabledHint', 'Enable SMS to configure sender and templates.')}
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.sms.senderId', 'SMS Sender ID')}</label>
                <p className="text-xs text-gray-500 mb-2">
                  {t('settings.sms.senderHelp', 'UP Send requires up to 11 Latin letters or digits with no spaces, or up to 14 digits. Leave blank to derive it from the business name.')}
                </p>
                <input
                  type="text"
                  value={smsSettings.sms_sender_id ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSmsSettings({ ...smsSettings, sms_sender_id: val });
                    setSettings({ ...settings, sms_sender_id: val });
                  }}
                  placeholder={(settings.business_name || 'DadaCoffee').replace(/[^A-Za-z0-9]/g, '').slice(0, 11) || 'DadaCoffee'}
                  disabled={!smsSettings.sms_enabled}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.sms.messagingServiceSid', 'Twilio Messaging Service SID')}</label>
                <input
                  type="text"
                  value={smsSettings.sms_messaging_service_sid ?? ''}
                  onChange={(e) => setSmsSettings({ ...smsSettings, sms_messaging_service_sid: e.target.value })}
                  placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  disabled={!smsSettings.sms_enabled}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-gray-50"
                />
                <p className="mt-1 text-xs text-gray-400">{t('settings.sms.messagingServiceSidDesc', 'Optional: Use a specific Twilio Messaging Service for better delivery.')}</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('settings.sms.whatsappHandoff', 'WhatsApp Handoff')}</label>
                  <p className="text-xs text-gray-400">{t('settings.sms.whatsappHandoffDesc', 'Include a link to WhatsApp in the SMS for easier communication.')}</p>
                </div>
                <ToggleSwitch
                  checked={smsSettings.sms_whatsapp_handoff_enabled}
                  onChange={(val) => {
                    setSmsSettings({ ...smsSettings, sms_whatsapp_handoff_enabled: val });
                    setSettings({ ...settings, sms_whatsapp_handoff_enabled: val });
                  }}
                  size="sm"
                  disabled={!smsSettings.sms_enabled}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.sms.expiration', 'Default Expiration (days)')}</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={smsSettings.default_payment_request_expiration_days}
                  onChange={(e) => {
                    const val = Number(e.target.value) || 14;
                    setSmsSettings({ ...smsSettings, default_payment_request_expiration_days: val });
                    setSettings({ ...settings, default_payment_request_expiration_days: val });
                  }}
                  disabled={!smsSettings.sms_enabled}
                  className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                data-testid="sms-save-btn"
                onClick={handleSmsSave}
                disabled={smsSaving}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {smsSaving ? t('common.loading') : t('common.save')}
              </button>
              {smsSaved && <span className="text-sm text-green-600 font-medium">✓ {t('settings.saved')}</span>}
              {!smsSettings.is_configured && smsSettings.sms_enabled && (
                <span className="text-xs text-amber-600 font-medium">⚠️ {t('settings.sms.notConfiguredHint', 'Provider credentials missing in server environment.')}</span>
              )}
            </div>
          </div>
        )}
      </AccordionCard>

      {canManageMembers && (
        <AccordionCard
          icon="💬"
          title={t('settings.messageTemplates.title', 'Message Templates')}
          description={t('settings.messageTemplates.description', 'Customer-facing Hebrew SMS and WhatsApp text')}
        >
          <MessageTemplatesSettings />
        </AccordionCard>
      )}

      {/* ── Orders Module ── */}
      <AccordionCard
        icon="🛒"
        title={t('modules.orders.name', 'Orders')}
        description={t('modules.orders.description', 'Order analysis and group chat settings')}
        moduleId="orders"
      >
        {chats.filter(c => c.chat_type === 'group').length === 0 ? (
          <p className="text-sm text-gray-500">{t('settings.chatManagement')} — {t('common.loading')}</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Configure which groups are used for order analysis:</p>
            <div className="space-y-2">
              {chats.filter(c => c.chat_type === 'group').map((chat) => (
                <div key={chat.id} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span>👥</span>
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{chat.name}</span>
                    {chat.participants_count && chat.participants_count > 1 && (
                      <span className="text-[10px] text-gray-400">({chat.participants_count})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleChatUpdate(chat.id, { is_order_analysis_group: !chat.is_order_analysis_group })}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        chat.is_order_analysis_group ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {chat.is_order_analysis_group ? 'Enabled' : 'Off'}
                    </button>
                    <button
                      onClick={() => handleRunAnalysis(chat.id)}
                      disabled={runningAnalysisChatId === chat.id || !chat.is_order_analysis_group}
                      className="rounded-md border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                    >
                      {runningAnalysisChatId === chat.id ? 'Running…' : 'Run now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </AccordionCard>

      {/* ── AI Agent Module ── */}
      <AccordionCard
        icon="✦"
        title={t('modules.ai_agent.name', 'AI Agent')}
        description={t('modules.ai_agent.description', 'Customer bot and signal extraction settings')}
        moduleId="ai_agent"
      >
        {/* Customer Bot */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">{t('customerBot.enabled')}</label>
              <p className="text-xs text-gray-400">{t('customerBot.enabledDesc')}</p>
            </div>
            <ToggleSwitch
              checked={settings.customer_bot_enabled}
              onChange={(val) => setSettings({ ...settings, customer_bot_enabled: val })}
              size="md"
              aria-label={t('customerBot.enabled')}
            />
          </div>

          <div className={`space-y-4 transition-opacity ${customerBotFieldsDisabled ? 'opacity-60' : ''}`}>
            {customerBotFieldsDisabled && (
              <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
                {t('customerBot.disabledHint')}
              </p>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('customerBot.businessName')}</label>
              <input type="text" value={settings.customer_bot_business_name ?? ''} onChange={(e) => setSettings({ ...settings, customer_bot_business_name: e.target.value })} placeholder={t('customerBot.defaultBusinessName')} disabled={customerBotFieldsDisabled} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-50" />
              <p className="mt-1 text-xs text-gray-400">{t('customerBot.businessNameDesc')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('customerBot.consentMessageVersion')}</label>
              <input type="text" value={settings.opt_in_consent_message_version ?? ''} onChange={(e) => setSettings({ ...settings, opt_in_consent_message_version: e.target.value })} placeholder={t('customerBot.consentMessageVersionPlaceholder')} disabled={customerBotFieldsDisabled} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-50" />
              <p className="mt-1 text-xs text-gray-400">{t('customerBot.consentMessageVersionDesc')}</p>
            </div>
          </div>
        </div>
      </AccordionCard>

      {/* ── Analytics Module ── */}
      <AccordionCard
        icon="📊"
        title={t('modules.analytics.name', 'Analytics')}
        description={t('modules.analytics.description', 'Reports and business analytics')}
        moduleId="analytics"
      >
        <DailySummarySettingsForm controller={dailySummary} showRecipients={false} />

        {/* Business Analysis schedule */}
        <div className="rounded-md border border-gray-100 bg-gray-50 p-4 space-y-3 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">{t('settings.permittedNumbers.businessAnalysisTitle')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('settings.permittedNumbers.businessAnalysisDescription')}</p>
            </div>
            <span
              className={(!permittedLoading && !permittedNumbers.some((n) => n.status === 'verified' && n.daily_summary)) ? 'cursor-not-allowed' : ''}
              title={!permittedNumbers.some((n) => n.status === 'verified' && n.daily_summary) ? t('reports.whatsappSummary.noEnabledRecipientsHint') : t('settings.permittedNumbers.businessAnalysisSendNowTooltip')}
            >
              <button
                type="button"
                onClick={handleSendBusinessAnalysisNow}
                disabled={baSending || !permittedNumbers.some((n) => n.status === 'verified' && n.daily_summary)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
              >
                {baSending ? '…' : t('settings.permittedNumbers.businessAnalysisSendNow')}
              </button>
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-[160px_100px_100px_1fr] sm:items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.permittedNumbers.businessAnalysisSendDay')}</label>
              <select value={baDay} onChange={(e) => setBaDay(Number(e.target.value))} className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none bg-white">
                {[0, 1, 2, 3, 4, 5, 6].map((d) => {
                  const labels = [t('settings.permittedNumbers.daySun'), t('settings.permittedNumbers.dayMon'), t('settings.permittedNumbers.dayTue'), t('settings.permittedNumbers.dayWed'), t('settings.permittedNumbers.dayThu'), t('settings.permittedNumbers.dayFri'), t('settings.permittedNumbers.daySat')];
                  return <option key={d} value={d}>{labels[d]}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.permittedNumbers.scheduleHour')}</label>
              <select value={baHour} onChange={(e) => setBaHour(Number(e.target.value))} className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none bg-white">
                {Array.from({ length: 24 }, (_, i) => (<option key={i} value={i}>{String(i).padStart(2, '0')}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.permittedNumbers.scheduleMinute')}</label>
              <select value={baMinute} onChange={(e) => setBaMinute(Number(e.target.value))} className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none bg-white">
                {[0, 15, 30, 45].map((m) => (<option key={m} value={m}>{String(m).padStart(2, '0')}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.permittedNumbers.scheduleTimezone')}</label>
              <select value={baTimezone} onChange={(e) => setBaTimezone(e.target.value)} className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none bg-white">
                <option value="Asia/Jerusalem">Asia/Jerusalem (IST)</option>
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                <option value="America/New_York">America/New_York (ET)</option>
                <option value="America/Chicago">America/Chicago (CT)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={handleSaveBusinessAnalysisSchedule} disabled={baLoading} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {baLoading ? t('settings.permittedNumbers.businessAnalysisScheduleSaving') : t('settings.permittedNumbers.businessAnalysisScheduleSave')}
            </button>
            {baMessage && <p className="text-xs font-medium text-green-700">✓ {baMessage}</p>}
            {baError && <p className="text-xs font-medium text-red-600">✕ {baError}</p>}
          </div>
        </div>
      </AccordionCard>

      {/* ── Claude MCP Integration ── */}
      <AccordionCard
        icon="🤖"
        title="Claude MCP Integration"
        description="Connect Claude Desktop or any MCP client to this workspace"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-600">
            Generate a per-workspace API key so Claude can query your data directly via the Model Context Protocol.
          </p>
          <span className={`ml-3 shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
            mcpKeyStatus?.has_key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${mcpKeyStatus?.has_key ? 'bg-green-500' : 'bg-gray-400'}`} />
            {mcpKeyStatus?.has_key ? 'Active' : 'No key'}
          </span>
        </div>

        {mcpKeyStatus?.has_key && !mcpGeneratedKey && (
          <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600 mb-3">
            <span className="font-mono">{mcpKeyStatus.key_prefix}</span>
            {mcpKeyStatus.created_at && (
              <span className="ml-2 text-xs text-gray-400">
                created {new Date(mcpKeyStatus.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
        )}

        {mcpGeneratedKey && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 mb-3 space-y-3">
            <div>
              <p className="text-xs font-medium text-amber-800 mb-1">
                Copy this key now — it will not be shown again.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={mcpGeneratedKey}
                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 font-mono text-xs text-gray-800 focus:outline-none"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={() => handleCopyCMcpKey(mcpGeneratedKey)}
                  className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  {mcpKeyCopied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="border-t border-amber-200 pt-3">
              <div className="flex items-center gap-4 mb-3">
                <button
                  type="button"
                  onClick={() => setMcpTarget('desktop')}
                  className={`text-xs font-semibold pb-1 border-b-2 transition-colors ${
                    mcpTarget === 'desktop' ? 'border-amber-600 text-amber-900' : 'border-transparent text-amber-600/60 hover:text-amber-700'
                  }`}
                >
                  Claude Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setMcpTarget('web')}
                  className={`text-xs font-semibold pb-1 border-b-2 transition-colors ${
                    mcpTarget === 'web' ? 'border-amber-600 text-amber-900' : 'border-transparent text-amber-600/60 hover:text-amber-700'
                  }`}
                >
                  Claude.ai (Web)
                </button>
              </div>

              {mcpTarget === 'desktop' ? (
                <>
                  <p className="text-xs text-amber-700 mb-2">
                    Add to <code className="bg-amber-100 px-1 rounded">~/.claude/claude_desktop_config.json</code>:
                  </p>
                  {(() => {
                    const configJson = `{
  "mcpServers": {
    "dada": {
      "type": "http",
      "url": "${window.location.origin}/api/mcp",
      "headers": {
        "Authorization": "Bearer ${mcpGeneratedKey}"
      }
    }
  }
}`;
                    return (
                      <div className="relative group">
                        <pre
                          dir="ltr"
                          className="rounded-md bg-gray-800 text-green-300 text-xs p-3 overflow-x-auto whitespace-pre"
                        >
                          {configJson}
                        </pre>
                        <button
                          type="button"
                          onClick={() => handleCopyMcpConfig(configJson)}
                          className="absolute top-2 ltr:right-2 rtl:left-2 rounded-md bg-gray-700 px-2 py-1 text-[10px] text-gray-300 hover:text-white transition-opacity"
                        >
                          {mcpConfigCopied ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-amber-700">
                    1. Go to <strong>Claude.ai</strong> and open <strong>Settings &gt; Connectors</strong>.
                    <br />
                    2. Click <strong>"Add custom connector"</strong> (requires Pro, Team, or Enterprise plan).
                    <br />
                    3. Fill in the following details:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Endpoint URL</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          readOnly
                          dir="ltr"
                          value={`${window.location.origin}/api/mcp`}
                          className="flex-1 rounded border border-amber-200 bg-white/50 px-2 py-1 text-xs font-mono"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Auth Header</label>
                      <input
                        type="text"
                        readOnly
                        dir="ltr"
                        value={`Authorization: Bearer ${mcpGeneratedKey}`}
                        className="w-full rounded border border-amber-200 bg-white/50 px-2 py-1 text-xs font-mono"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-amber-600/80 italic">
                    Note: Your workspace must be publicly accessible (e.g., on a production domain) for Claude.ai to connect.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {mcpKeyError && <p className="text-sm font-medium text-red-600 mb-2">✕ {mcpKeyError}</p>}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleGenerateMcpKey}
            disabled={mcpKeyLoading}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {mcpKeyLoading ? 'Generating…' : mcpKeyStatus?.has_key ? 'Regenerate key' : 'Generate key'}
          </button>

          {mcpKeyStatus?.has_key && !mcpRevokeConfirm && (
            <button
              type="button"
              onClick={() => setMcpRevokeConfirm(true)}
              disabled={mcpKeyLoading}
              className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              Revoke key
            </button>
          )}

          {mcpRevokeConfirm && (
            <>
              <span className="text-sm text-gray-600">Revoke the active key?</span>
              <button
                type="button"
                onClick={handleRevokeMcpKey}
                disabled={mcpKeyLoading}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Yes, revoke
              </button>
              <button
                type="button"
                onClick={() => setMcpRevokeConfirm(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </AccordionCard>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-end gap-3">
          {saved && <span className="text-sm text-green-600 font-medium">✓ {t('settings.saved')}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Resync confirmation modal */}
      {rivhitResyncModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('settings.rivhit.resyncModalTitle', 'Resync Rivhit Data')}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('settings.rivhit.resyncModalBody', 'This will clear the locally cached Rivhit data in Dada Agent and reload everything from your Rivhit account. No data will be deleted from Rivhit itself.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRivhitResyncModalOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleRivhitResync}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
              >
                {t('settings.rivhit.resyncConfirmButton', 'Resync')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
